/**
 * The generator's "Choose a picture", for the portal: a file from this
 * computer, scaled down and handed back as a `data:` URI to embed.
 *
 * Embedding is right here even though the portal is a web page: it never sends
 * mail itself. The signature goes through UMG to the mail apps, and they turn
 * `data:` images into `cid:` parts when they send, the way mobile-client's own
 * gallery pick already travels.
 *
 * The rules are mobile-client's (`components/picture-gallery/imagePolicy.ts`),
 * so a picture chosen here and one chosen on a phone come out the same: 2× the
 * largest size any template draws, JPEG for a photograph, PNG for a logo or
 * banner that may need its transparency, and a ceiling on the bytes every
 * message in a thread will carry.
 */

export type ImageSlot = 'avatar' | 'logo' | 'banner';

/** Longest side, in pixels: 2× the largest size any template draws. */
export const MAX_IMAGE_SIDE = 320;

/** The ceiling for one embedded image, in decoded bytes. */
export const MAX_EMBED_BYTES = 120 * 1024;

/** JPEG quality for a photograph at this size. */
export const JPEG_QUALITY = 0.85;

/** What the file dialog offers. WebP is absent on purpose: Outlook does not draw it. */
export const ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif';

export function formatForSlot(slot: ImageSlot): 'image/jpeg' | 'image/png' {
  return slot === 'avatar' ? 'image/jpeg' : 'image/png';
}

/** Decoded size of a data URI's base64 payload, without decoding it. */
export function decodedBytes(dataUri: string): number {
  const base64 = dataUri.slice(dataUri.indexOf(',') + 1);
  return (base64.length * 3) / 4;
}

/** The picture's size once its longest side fits MAX_IMAGE_SIDE; never scaled up. */
export function scaledSize(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(width, height, 1));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Opens the file dialog; null when nothing was chosen. */
function chooseFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPTED_TYPES;
    input.addEventListener('change', () => resolve(input.files?.[0] ?? null), { once: true });
    input.addEventListener('cancel', () => resolve(null), { once: true });
    input.click();
  });
}

function decode(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('the picture could not be read'));
    img.src = url;
  }).finally(() => URL.revokeObjectURL(url));
}

function encode(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png'): string | null {
  const out = type === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return decodedBytes(out) > MAX_EMBED_BYTES ? null : out;
}

/**
 * Resolves to the chosen picture as a data URI, or null when the person
 * cancelled. Throws with a message a person can read when the picture cannot be
 * used, so the page can say why nothing happened.
 */
export async function pickImage(slot: ImageSlot): Promise<string | null> {
  const file = await chooseFile();
  if (!file) return null;

  const img = await decode(file);
  const size = scaledSize(img.naturalWidth, img.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('this browser cannot resize pictures');
  ctx.drawImage(img, 0, 0, size.width, size.height);

  const wanted = formatForSlot(slot);
  // A PNG too heavy to send falls back to JPEG, as on the phone.
  const out = encode(canvas, wanted) ?? (wanted === 'image/png' ? encode(canvas, 'image/jpeg') : null);
  if (!out) throw new Error('the picture is too large to put in a signature, even scaled down');
  return out;
}
